/*
  <kit-parts>: kit parts shown side by side at true relative scale (snippets/mos-kit-parts.liquid).
  - Layout: every part is sized from its real height (data-mm) with one pixels-per-mm value.
  - Scroll reveal: parts rise in, then boxes + pins one after another; the default part stays selected.
  - Dot grid: faint dots grow and turn blue near the finger/cursor. While zoomed, dots fade to 15%
    next to the focused part and back to full one part-radius out.
  - Lens: "Read more" opens a full-screen camera that grows out of the stage and zooms onto the part.
*/
if (!customElements.get('kit-parts')) {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  const pad = (n) => String(n + 1).padStart(2, '0');
  const DOT = { gap: 18, r: 1.2, grow: 1.7, reach: 90, base: [21, 23, 35, 0.16], hot: [40, 69, 255, 0.9] };
  const DESK = 880;

  /* ---------- dot field (one per canvas) ---------- */
  class DotField {
    constructor(canvas) {
      Object.assign(this, { c: canvas, ox: 0, oy: 0, p: null, e: null, cols: 0, rows: 0, raf: 0, focus: null, tweening: false });
    }
    kick() {
      if (!this.raf) this.raf = requestAnimationFrame(() => this.frame());
    }
    frame() {
      this.raf = 0;
      const c = this.c, d = Math.min(devicePixelRatio || 1, 2), w = c.clientWidth, h = c.clientHeight;
      if (!w || !h) return;
      if (c.width !== Math.round(w * d) || c.height !== Math.round(h * d)) { c.width = Math.round(w * d); c.height = Math.round(h * d); }
      const g = DOT.gap, sx = ((this.ox % g) + g) % g, sy = ((this.oy % g) + g) % g;
      const cols = Math.ceil(w / g) + 2, rows = Math.ceil(h / g) + 2;
      if (!this.e || this.cols !== cols || this.rows !== rows) { this.e = new Float32Array(cols * rows); this.cols = cols; this.rows = rows; }
      const x = c.getContext('2d');
      x.setTransform(d, 0, 0, d, 0, 0); x.clearRect(0, 0, w, h);
      const a = DOT.base, b = DOT.hot, p = this.p, R2 = DOT.reach * DOT.reach, f = this.focus;
      let busy = false;
      for (let j = 0; j < rows; j++) for (let i = 0; i < cols; i++) {
        const k = j * cols + i, px = sx - g + i * g, py = sy - g + j * g;
        let inf = 0;
        if (p) { const dx = px - p.x, dy = py - p.y; inf = Math.exp(-(dx * dx + dy * dy) / R2); }
        let e = Math.max(this.e[k] * (reduce ? 0 : 0.93), inf);
        if (e < 0.004) e = 0;
        this.e[k] = e; if (e) busy = true;
        let vis = 1;
        if (f && f.s > 0.001) {
          const ux = px - f.x, uy = py - f.y, D = Math.hypot(ux, uy), de = Math.hypot(ux / f.rx, uy / f.ry) || 1e-6;
          const q = Math.min(1, Math.max(0, (D - D / de) / ((f.rx + f.ry) / 2))); // px beyond the outline / average radius
          vis = 1 - f.s * 0.85 * (1 - q * q * (3 - 2 * q));                     // 15% floor next to the part
        }
        const m = (n) => a[n] + (b[n] - a[n]) * e;
        x.fillStyle = `rgba(${m(0) | 0},${m(1) | 0},${m(2) | 0},${m(3) * Math.max(vis, e)})`;
        x.beginPath(); x.arc(px, py, DOT.r + e * DOT.grow, 0, 6.2832); x.fill();
      }
      if (busy || p || this.tweening) this.kick();
    }
    track(host) {
      const at = (cx, cy) => { const r = this.c.getBoundingClientRect(); this.p = { x: cx - r.left, y: cy - r.top }; this.kick(); };
      const off = () => { this.p = null; this.kick(); };
      host.addEventListener('pointermove', (e) => { if (e.pointerType === 'mouse') at(e.clientX, e.clientY); });
      host.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') off(); });
      host.addEventListener('touchstart', (e) => at(e.touches[0].clientX, e.touches[0].clientY), { passive: true });
      host.addEventListener('touchmove', (e) => at(e.touches[0].clientX, e.touches[0].clientY), { passive: true });
      host.addEventListener('touchend', off, { passive: true });
      host.addEventListener('touchcancel', off, { passive: true });
    }
  }

  class KitParts extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const q = (s) => this.querySelector(s);
      this.stage = q('[data-stage]');
      this.world = q('[data-world]');
      this.chipsEl = q('[data-chips]');
      this.leads = q('[data-leads]');
      this.lens = q('[data-lens]');
      this.sheetBody = this.lens.querySelector('[data-sheet-body]');
      this.details = [...this.querySelectorAll('[data-detail]')];
      document.body.appendChild(this.lens); // escape any transformed/overflow ancestor

      const L = this.lens.querySelector.bind(this.lens);
      this.ui = {
        count: L('[data-count]'), ticks: [...L('[data-ticks]').children], hint: L('[data-hint]'), sheet: L('[data-sheet]'),
        prevName: L('[data-prev-name]'), nextName: L('[data-next-name]'), close: L('[data-close]'), grab: L('[data-grab]'),
      };
      const chips = [...this.chipsEl.querySelectorAll('.kp__chip')];
      const pins = [...this.world.querySelectorAll('.kp__pin')];
      this.parts = [...this.world.querySelectorAll('.kp__part')].map((el, i) => {
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        dot.setAttribute('r', '2.5');
        this.leads.append(path, dot);
        return {
          el, pin: pins[i], chip: chips[i], path, dot, mm: parseFloat(el.dataset.mm) || 100, ar: parseFloat(el.dataset.ar) || 0.3,
          name: chips[i].querySelector('.kp__chip-title').textContent.trim(),
        };
      });
      this.n = this.parts.length;
      this.stage.classList.toggle('is-compact', this.n > 4); // 5+ parts: compact boxes on phones
      this.def = Math.min(parseInt(this.dataset.default, 10) || 0, this.n - 1);
      this.hot = -1; this.revealed = 0; this.cur = 0; this.isOpen = false; this.busy = false;

      this.sf = new DotField(q('[data-dots]'));
      this.lf = new DotField(this.lens.querySelector('[data-lens-dots]'));
      this.sf.track(this.stage); this.lf.track(this.lens);
      this.F = { x: 0, y: 0, s: 0, rx: 100, ry: 100 };

      this.bind();
      this.layout();
      if (document.fonts) document.fonts.ready.then(() => this.layout());

      new IntersectionObserver((es, io) => {
        if (!es.some((e) => e.isIntersecting)) return;
        io.disconnect();
        this.reveal();
      }, { threshold: 0.35 }).observe(this.stage);
    }

    revealAt(i) { return 670 + i * 470; } // ms after the section enters view

    reveal() {
      this.stage.classList.add('is-in');
      this.parts.forEach((p, i) => {
        setTimeout(() => { this.revealed = i + 1; if (this.desk) this.showLeads(); else this.setHot(i); }, reduce ? 0 : this.revealAt(i) + 120);
      });
      setTimeout(() => this.setHot(this.def), reduce ? 0 : this.revealAt(this.n) + 200);
    }

    /* ---------- layout: everything placed in px from a pixels-per-mm value ---------- */
    layout() {
      const W = this.stage.clientWidth, H = this.stage.clientHeight, desk = innerWidth >= DESK;
      this.desk = desk;
      const chipsH = desk ? 0 : this.chipsEl.offsetHeight;
      const floorPad = desk ? 56 : 46;
      const topRoom = desk ? 190 : chipsH + 30;
      const tallest = Math.max(...this.parts.map((p) => p.mm));
      const sumMM = this.parts.reduce((a, p) => a + p.mm * p.ar, 0);
      const gaps = this.n - 1;
      let ppm = (H - topRoom - floorPad) / tallest;
      const sidePad = desk ? (this.n > 4 ? 100 : 120) : 26; // room for the end boxes, which are wider than the end parts
      let gapPx, chipW = 212;
      if (desk) {
        // Boxes sit above their own part, so neighbouring parts must be far enough apart for the boxes not to overlap.
        // Bigger kits get smaller gaps (down to 40px); if boxes would still be under 150px wide, the parts shrink instead.
        const pairs = this.parts.slice(1).map((p, i) => (this.parts[i].mm * this.parts[i].ar + p.mm * p.ar) / 2);
        for (let k = 0; k < 20; k++) {
          gapPx = Math.min(Math.max(150, W * 0.12), Math.max(40, (W - sidePad * 2 - sumMM * ppm) / (gaps || 1)));
          chipW = Math.min(212, Math.min(...pairs) * ppm + gapPx - 12);
          if (chipW >= 150 && sumMM * ppm + gapPx * gaps <= W - sidePad * 2) break;
          ppm *= 0.94;
        }
      } else {
        gapPx = 12 * ppm;
        ppm = Math.min(ppm, (W - sidePad * 2 - gapPx * gaps) / sumMM);
        // spread the parts across the full width when they're narrower than the stage (slim parts / 5+ kits)
        gapPx = Math.max(12 * ppm, (W - sidePad * 2 - sumMM * ppm) / (gaps || 1));
      }
      const floorY = H - floorPad;
      let x = (W - (sumMM * ppm + gapPx * gaps)) / 2;
      this.world.style.width = W + 'px'; this.world.style.height = H + 'px';
      this.parts.forEach((p, i) => {
        const h = p.mm * ppm, w = h * p.ar;
        p.box = { x, y: floorY - h, w, h };
        Object.assign(p.el.style, { left: x + 'px', top: floorY - h + 'px', width: w + 'px', height: h + 'px' });
        const py = floorY - h + h * (desk ? 0.06 : 0.32);
        p.pinXY = [x + w / 2, py];
        Object.assign(p.pin.style, { left: x + w / 2 + 'px', top: py + 'px' });
        p.pin.style.setProperty('--pd', this.revealAt(i) + 'ms');
        p.chip.style.setProperty('--cd', this.revealAt(i) + 'ms');
        x += w + gapPx;
      });
      this.parts.forEach((p) => {
        if (desk) {
          const cw = chipW, cx = Math.min(Math.max(p.pinXY[0] - cw / 2, 16), W - cw - 16);
          p.chip.style.width = cw + 'px'; p.chip.style.left = cx + 'px'; p.chip.style.top = '0px';
          p.chip.style.top = Math.max(16, p.box.y - 58 - p.chip.offsetHeight) + 'px';
        } else { p.chip.style.left = ''; p.chip.style.top = ''; p.chip.style.width = ''; }
      });
      this.sf.kick();
      this.drawLeads();
    }

    /* leader lines: box → pin (orthogonal routing) */
    drawLeads() {
      const sr = this.stage.getBoundingClientRect();
      this.leads.setAttribute('viewBox', `0 0 ${sr.width} ${sr.height}`);
      this.parts.forEach((p, i) => {
        const cr = p.chip.getBoundingClientRect();
        const sx = cr.left - sr.left + cr.width / 2, sy = cr.bottom - sr.top, [px, py] = p.pinXY;
        const my = this.chipsEl.offsetHeight + 12 + (i % 2) * 8;
        p.path.setAttribute('d', this.desk ? `M${px} ${sy} V${py - 13}` : `M${sx} ${sy} V${my} H${px} V${py - 13}`);
        const len = p.path.getTotalLength();
        p.path.style.strokeDasharray = len; p.path.style.setProperty('--len', len);
        p.dot.setAttribute('cx', this.desk ? px : sx); p.dot.setAttribute('cy', sy);
      });
      this.showLeads();
    }
    showLeads() {
      const inView = this.stage.classList.contains('is-in');
      this.parts.forEach((p, i) => {
        const on = inView && (this.desk ? this.revealed > i : this.hot === i);
        p.path.classList.toggle('on', on); p.dot.classList.toggle('on', on);
        p.chip.classList.toggle('is-hot', this.hot === i);
        p.pin.classList.toggle('is-hot', this.hot === i);
      });
    }
    setHot(i, instant) {
      this.stage.classList.toggle('is-instant', !!instant); // taps switch right away; the intro keeps its animation
      this.hot = i; this.showLeads();
    }

    /* ---------- the lens / camera ---------- */
    region() {
      const W = document.documentElement.clientWidth, H = innerHeight;
      if (this.desk) return { x: 0, y: 0, w: W - 420, h: H };
      const sh = this.ui.sheet.offsetHeight;
      return { x: 0, y: 48, w: W, h: H - sh - 52 }; // product sits just under the counter/close bar
    }
    camera(i) {
      const b = this.parts[i].box, R = this.region();
      let s = Math.min((R.h * (this.desk ? 0.66 : 0.74)) / b.h, (R.w * 0.42) / b.w, 3.4);
      s = Math.min(Math.max(s, 1.15), (R.h * 0.86) / b.h); // always a zoom-in, never cropped
      const cx = R.x + R.w / 2, cy = R.y + R.h / 2 + (this.desk ? 0 : 6);
      return { s, cx, cy, tx: cx - this.from.left - s * (b.x + b.w / 2), ty: cy - this.from.top - s * (b.y + b.h / 2) };
    }
    applyCam(c) { this.world.style.transform = `translate(${c.tx}px,${c.ty}px) scale(${c.s})`; }
    focusOf(i, c) {
      const b = this.parts[i].box;
      return { x: c.cx, y: c.cy, rx: (b.w * c.s) / 2 + 8, ry: (b.h * c.s) / 2 + 8, s: 1 };
    }
    focusTo(T, dur, dip) {
      cancelAnimationFrame(this.tw);
      const F = this.F, lf = this.lf, A = { ...F }, t0 = performance.now(), D = reduce ? 0 : dur;
      lf.focus = F; lf.tweening = true;
      const step = (now) => {
        const k = D ? Math.min(1, (now - t0) / D) : 1, e = ease(k);
        for (const key in T) F[key] = A[key] + (T[key] - A[key]) * e;
        if (dip) F.s = T.s * (1 - 0.6 * Math.sin(Math.PI * k));
        lf.kick();
        if (k < 1) this.tw = requestAnimationFrame(step); else lf.tweening = false;
      };
      this.tw = requestAnimationFrame(step);
    }
    fill(i) {
      this.sheetBody.innerHTML = this.details[i] ? this.details[i].innerHTML : '';
      this.sheetBody.scrollTop = 0;
      this.ui.count.textContent = pad(i);
      this.ui.ticks.forEach((t, k) => t.classList.toggle('on', k === i));
      this.ui.prevName.textContent = this.parts[(i - 1 + this.n) % this.n].name;
      this.ui.nextName.textContent = this.parts[(i + 1) % this.n].name;
      this.parts.forEach((p, k) => { p.el.classList.toggle('is-dim', k !== i); p.el.tabIndex = k === i ? -1 : 0; });
    }
    open(i, btn) {
      if (this.isOpen || this.busy) return;
      const r = this.stage.getBoundingClientRect();
      if (r.top < -40 || r.bottom > innerHeight + 40) { // bring the stage into view first, then zoom
        this.stage.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
        setTimeout(() => this.open(i, btn), reduce ? 0 : 450);
        return;
      }
      this.busy = true; this.opener = btn; this.isOpen = true; this.cur = i; this.setHot(-1);
      const from = (this.from = this.stage.getBoundingClientRect());
      document.documentElement.classList.add('kp-locked');
      const vw = document.documentElement.clientWidth, rad = this.desk ? 24 : 0;
      this.lens.style.setProperty('--clip-from', `inset(${from.top}px ${vw - from.right}px ${innerHeight - from.bottom}px ${from.left}px round ${rad}px)`);
      this.lens.classList.remove('is-open'); this.lens.hidden = false;
      this.world.style.left = from.left + 'px'; this.world.style.top = from.top + 'px';
      this.lens.insertBefore(this.world, this.lf.c.nextSibling);
      this.applyCam({ s: 1, tx: 0, ty: 0 });
      this.fill(i);
      this.lens.offsetWidth; // commit start frame
      requestAnimationFrame(() => {
        this.lens.classList.add('is-open');
        const c = this.camera(i);
        this.applyCam(c); this.placeHint();
        Object.assign(this.F, this.focusOf(i, c), { s: 0 });
        this.lf.ox = from.left; this.lf.oy = from.top; this.lf.e = null;
        this.focusTo({ s: 1 }, 900);
        setTimeout(() => { this.busy = false; this.ui.close.focus({ preventScroll: true }); }, reduce ? 0 : 600);
      });
    }
    go(i) {
      if (!this.isOpen) return;
      i = (i + this.n) % this.n;
      if (i === this.cur) return;
      this.cur = i;
      this.sheetBody.classList.add('swap');
      const c = this.camera(i);
      this.applyCam(c); this.focusTo(this.focusOf(i, c), 750, true);
      setTimeout(() => { this.fill(i); this.sheetBody.classList.remove('swap'); }, reduce ? 0 : 200);
    }
    close() {
      if (!this.isOpen || this.busy) return;
      this.busy = true;
      this.lens.classList.remove('is-open');
      this.applyCam({ s: 1, tx: 0, ty: 0 }); this.focusTo({ s: 0 }, 500);
      this.parts.forEach((p) => p.el.classList.remove('is-dim'));
      setTimeout(() => {
        Object.assign(this.world.style, { left: '0', top: '0', transform: '' });
        this.stage.insertBefore(this.world, this.leads.nextSibling);
        this.lens.hidden = true; this.isOpen = false; this.busy = false;
        document.documentElement.classList.remove('kp-locked');
        if (this.opener) this.opener.focus({ preventScroll: true });
        this.setHot(this.def);
      }, reduce ? 0 : 760);
    }
    placeHint() {
      const R = this.region();
      this.ui.hint.style.top = R.y + R.h - 22 + 'px';
      this.ui.hint.hidden = this.desk;
    }

    bind() {
      this.chipsEl.addEventListener('click', (e) => {
        const m = e.target.closest('[data-open]');
        if (m) return this.open(+m.dataset.open, m);
        // tapping a box selects it; tapping the selected box again opens the zoom (phones and desktop)
        const c = e.target.closest('.kp__chip');
        if (!c) return;
        const i = +c.dataset.i;
        if (this.hot === i) this.open(i, c.querySelector('[data-open]'));
        else this.setHot(i, true);
      });
      this.world.addEventListener('click', (e) => {
        const p = e.target.closest('.kp__part');
        if (!p) return;
        if (this.isOpen) this.go(+p.dataset.i); else this.open(+p.dataset.i, p);
      });
      this.ui.close.addEventListener('click', () => this.close());
      this.lens.querySelector('[data-prev]').addEventListener('click', () => this.go(this.cur - 1));
      this.lens.querySelector('[data-next]').addEventListener('click', () => this.go(this.cur + 1));
      addEventListener('keydown', (e) => {
        if (!this.isOpen) return;
        if (e.key === 'Escape') this.close();
        if (e.key === 'ArrowRight') this.go(this.cur + 1);
        if (e.key === 'ArrowLeft') this.go(this.cur - 1);
      });
      /* swipe between parts; drag the sheet handle down to close */
      let sx = 0, sy = 0, st = 0, onSheet = false;
      this.lens.addEventListener('pointerdown', (e) => {
        sx = e.clientX; sy = e.clientY; st = Date.now();
        onSheet = !!e.target.closest('.kp__sheet-body, .kp__nav');
      });
      this.lens.addEventListener('pointerup', (e) => {
        const dx = e.clientX - sx, dy = e.clientY - sy;
        if (e.target.closest('[data-grab]') && dy > 50) return this.close();
        if (onSheet) return;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.3 && Date.now() - st < 700) this.go(this.cur + (dx < 0 ? 1 : -1));
      });
      this.lens.addEventListener('touchmove', (e) => { if (!e.target.closest('.kp__sheet-body')) e.preventDefault(); }, { passive: false });

      let lastW = innerWidth;
      addEventListener('resize', () => {
        if (innerWidth !== lastW && this.isOpen) { this.busy = false; this.close(); }
        lastW = innerWidth;
        if (!this.isOpen) this.layout();
      });
    }
  }

  customElements.define('kit-parts', KitParts);
}
